import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Alert, Button, Drawer, Descriptions, Form, Input, InputNumber, Popconfirm, Space, Switch, Table, Tag, Tooltip,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import actions from "../../redux/admin/actions";
import { adminTimestampMeta, formatAdminDateTime } from "../../dates";
import { showAdminFeedback } from "../../feedback";
import Wrapper from "./index.style";

const TIER_COLORS = {
  admin: "purple", organization: "geekblue", individual: "blue", premium: "gold", unlicensed: "default", guest: "default",
  trial: "cyan",
};
const {
  fetchUsers,
  fetchUser,
  clearUserDetail,
  updateUser,
  extendTrial,
  deleteUser,
  resendInvitation,
  clearFeedback,
} = actions;

export class UsersPanel extends Component {
  state = { query: "" };

  componentDidMount() {
    this.props.fetchUsers({ q: "", page: 1, per_page: 25 });
  }

  componentDidUpdate(prevProps) {
    const { feedback, clearFeedback, t } = this.props;
    if (feedback && feedback !== prevProps.feedback) {
      showAdminFeedback(feedback, t);
      clearFeedback();
    }
  }

  requestParams = (page = this.props.pagination.page || 1, q = this.state.query) => ({
    q, page, per_page: 25,
  });

  toggleDisabled = (user, disabled) => {
    this.props.updateUser(user.id, { disabled }, this.requestParams(), {
      type: "success",
      messageKey: disabled ? "admin.users.accountDisabled" : "admin.users.accountEnabled",
    });
  };

  grantFeature = (user, key, value) => {
    const flags = { ...(user.feature_flags || {}), [key]: value };
    this.props.updateUser(user.id, { feature_flags: flags }, this.requestParams(), {
      type: "success",
      messageKey: value ? "admin.users.featureGranted" : "admin.users.featureRevoked",
      values: { feature: key },
      refreshDetail: true,
    });
  };

  deleteUser = (user) => {
    this.props.deleteUser(user.id, user.display_name || user.email, this.requestParams());
  };

  toggleAdmin = (user, admin) => {
    this.props.updateUser(user.id, { admin }, this.requestParams(), {
      type: "success",
      messageKey: admin ? "admin.users.adminGranted" : "admin.users.adminRevoked",
    });
  };

  resendInvite = (invite) => {
    this.props.resendInvitation(invite.organization_id, invite.id, invite.email, "users");
  };

  // gos-admin's table idiom: lead with "how long ago", keep the exact
  // moment a hover away.
  renderTimestamp = (value, timezone) => {
    const { isValid, relative, full } = adminTimestampMeta(
      value, timezone, this.props.i18n.language,
    );

    return isValid ? <Tooltip title={full}>{relative}</Tooltip> : relative;
  };

  render() {
    const {
      t, i18n, rows, invitations, pagination, loading, error, detail, operator, mutationLoading,
      fetchUsers, fetchUser, clearUserDetail,
    } = this.props;
    const invitationColumns = [
      { title: t("admin.common.email"), dataIndex: "email" },
      {
        title: t("admin.common.name"),
        render: (invite) => `${invite.first_name || ""} ${invite.last_name || ""}`.trim() || t("admin.common.notAvailable"),
      },
      {
        title: t("admin.common.role"), dataIndex: "role", width: 130,
        render: (role) => <Tag>{t(role === "org_admin" ? "admin.common.orgAdmin" : "admin.common.member")}</Tag>,
      },
      { title: t("admin.common.organization"), dataIndex: "organization_name", width: 180 },
      {
        title: t("admin.common.status"), dataIndex: "status", width: 100,
        render: (status) => (
          <Tag color={status === "pending" ? "orange" : "default"}>{t(`admin.common.statuses.${status}`)}</Tag>
        ),
      },
      {
        title: t("admin.users.emailDelivery"), width: 190,
        render: (invite) => invite.last_sent_at ? (
          <span>
            <Tag color="green">{t("admin.common.statuses.sent")}</Tag>
            {this.renderTimestamp(invite.last_sent_at, invite.organization_timezone)}
          </span>
        ) : <Tag color="blue">{t("admin.common.statuses.queued")}</Tag>,
      },
      {
        title: t("admin.common.action"), width: 100,
        render: (invite) => (
          <Button size="small" onClick={() => this.resendInvite(invite)}>{t("admin.common.resend")}</Button>
        ),
      },
    ];
    const columns = [
      {
        title: t("admin.common.email"), dataIndex: "email",
        render: (email, user) => (
          <Button type="link" className="table-link" onClick={() => fetchUser(user.id)}>{email}</Button>
        ),
      },
      { title: t("admin.common.name"), dataIndex: "display_name" },
      {
        title: t("admin.users.tier"), dataIndex: "tier", width: 130,
        render: (tier) => <Tag color={TIER_COLORS[tier]}>{t(`admin.common.tiers.${tier}`)}</Tag>,
      },
      { title: t("admin.common.organization"), dataIndex: "organization_name", width: 160 },
      {title: t("admin.users.signedUp"), dataIndex: "created_at", width: 170,
        render: (value, user) => <Tooltip title={formatAdminDateTime(value, user.organization_timezone, i18n.language)}>{this.renderTimestamp(value, user.organization_timezone)}</Tooltip>},
      {title: t("admin.users.accountStatus"), dataIndex: "account_status", width: 160,
        render: status => t(`admin.users.accountStatuses.${status || "unlicensed"}`)},
      {title: t("admin.users.trialRemaining"), width: 150,
        render: user => user.trial?.ends_at ? <Tooltip title={formatAdminDateTime(user.trial.ends_at, "Europe/Athens", i18n.language)}>{t("admin.users.daysRemaining", {count: user.trial.days_remaining})}</Tooltip> : "—"},
      { title: t("admin.users.signIns"), dataIndex: "sign_in_count", width: 90 },
      {
        title: t("admin.users.enabled"), dataIndex: "disabled", width: 100,
        render: (disabled, user) => (
          <Popconfirm
            title={t(disabled ? "admin.users.confirmEnable" : "admin.users.confirmDisable")}
            okText={t("admin.common.confirm")}
            cancelText={t("admin.common.cancel")}
            onConfirm={() => this.toggleDisabled(user, !disabled)}
          >
            <Switch checked={!disabled} size="small" />
          </Popconfirm>
        ),
      },
      {
        title: t("admin.users.admin"), dataIndex: "roles", width: 100,
        render: (roles, user) => (
          <Popconfirm
            title={t(roles.includes("admin") ? "admin.users.confirmRevokeAdmin" : "admin.users.confirmGrantAdmin")}
            okText={t("admin.common.confirm")}
            cancelText={t("admin.common.cancel")}
            onConfirm={() => this.toggleAdmin(user, !roles.includes("admin"))}
          >
            <Switch checked={roles.includes("admin")} size="small" />
          </Popconfirm>
        ),
      },
      {
        title: t("admin.common.action"), width: 120,
        render: (user) => {
          // The API refuses this anyway; hiding it keeps an operator from
          // aiming the irreversible action at their own account.
          if (operator && operator.email === user.email) return null;

          return (
            <Popconfirm
              title={t("admin.users.deleteAccountTitle")}
              description={t("admin.users.deleteAccountWarning", {
                name: user.display_name || user.email,
              })}
              okText={t("admin.users.deleteAccountConfirm")}
              cancelText={t("admin.common.cancel")}
              okButtonProps={{ danger: true, loading: mutationLoading }}
              onConfirm={() => this.deleteUser(user)}
            >
              <Button danger icon={<DeleteOutlined />} size="small">
                {t("admin.users.deleteAccount")}
              </Button>
            </Popconfirm>
          );
        },
      },
    ];

    return (
      <Wrapper>
        {error && <Alert type="error" message={error} showIcon className="users-error" />}
        <Space className="search-row">
          <Input.Search
            placeholder={t("admin.users.searchPlaceholder")}
            allowClear
            className="user-search"
            onSearch={(value) => {
              this.setState({ query: value });
              fetchUsers(this.requestParams(1, value));
            }}
          />
        </Space>
        <h3>{t("admin.users.pendingInvitations", { count: invitations.length })}</h3>
        <Table
          rowKey="id"
          loading={loading}
          columns={invitationColumns}
          dataSource={invitations}
          pagination={false}
          size="middle"
          className="invitations-table"
        />
        <h3>{t("admin.users.registeredUsers")}</h3>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ current: pagination.page, pageSize: pagination.per_page, total: pagination.total }}
          onChange={(page) => fetchUsers(this.requestParams(page.current))}
          size="middle"
          scroll={{x: 1500}}
        />
        <Drawer open={!!detail} width={480} title={detail?.user?.email} onClose={clearUserDetail}>
          {detail && (
            <>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t("admin.common.name")}>{detail.user.display_name}</Descriptions.Item>
                <Descriptions.Item label={t("admin.users.signedUp")}>{formatAdminDateTime(detail.user.created_at, "Europe/Athens", i18n.language)}</Descriptions.Item>
                <Descriptions.Item label={t("admin.users.accountStatus")}>{t(`admin.users.accountStatuses.${detail.user.account_status || "unlicensed"}`)}</Descriptions.Item>
                <Descriptions.Item label={t("admin.users.emailVerified")}>{formatAdminDateTime(detail.user.email_verified_at, "Europe/Athens", i18n.language)}</Descriptions.Item>
                <Descriptions.Item label={t("admin.users.trialEnds")}>{formatAdminDateTime(detail.user.trial?.ends_at, "Europe/Athens", i18n.language)}</Descriptions.Item>
                <Descriptions.Item label={t("admin.users.tier")}>
                  <Tag color={TIER_COLORS[detail.entitlements.tier]}>
                    {t(`admin.common.tiers.${detail.entitlements.tier}`)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("admin.users.detail.roles")}>
                  {detail.user.roles.map((role) => t(
                    role === "org_admin" ? "admin.common.orgAdmin" : `admin.common.tiers.${role}`,
                  )).join(", ") || t("admin.common.notAvailable")}
                </Descriptions.Item>
                <Descriptions.Item label={t("admin.users.detail.oauthProvider")}>
                  {detail.user.provider || t("admin.users.detail.emailPassword")}
                </Descriptions.Item>
                <Descriptions.Item label={t("admin.users.detail.termsAccepted")}>
                  {formatAdminDateTime(
                    detail.user.terms_accepted_at, detail.user.organization_timezone, i18n.language,
                  )}
                </Descriptions.Item>
              </Descriptions>
              {["trial_active", "trial_expired"].includes(detail.user.account_status) && (
                <Form layout="vertical" onFinish={({days, reason}) => this.props.extendTrial(detail.user.id, days, reason, this.requestParams())} initialValues={{days: 3}}>
                  <h4>{t("admin.users.extendTrial")}</h4>
                  <Form.Item name="days" label={t("admin.users.additionalDays")} rules={[{required: true}]}><InputNumber min={1} max={365} precision={0} /></Form.Item>
                  <Form.Item name="reason" label={t("admin.users.extensionReason")} rules={[{required: true, whitespace: true}]}><Input.TextArea maxLength={500} /></Form.Item>
                  <Button type="primary" htmlType="submit" loading={mutationLoading}>{t("admin.users.extendTrial")}</Button>
                </Form>
              )}
              {detail.trial_extensions?.length > 0 && <>
                <h4>{t("admin.users.extensionHistory")}</h4>
                {detail.trial_extensions.map(event => <p key={event.id}>{formatAdminDateTime(event.created_at, "Europe/Athens", i18n.language)} · #{event.administrator_id || "—"} · {event.reason} · {formatAdminDateTime(event.ends_at, "Europe/Athens", i18n.language)}</p>)}
              </>}
              <h4 className="drawer-section-title">{t("admin.users.detail.perSeatGrants")}</h4>
              <Space>
                <Switch
                  size="small"
                  checked={detail.user.feature_flags?.nowcast_full !== false}
                  onChange={(checked) => this.grantFeature(detail.user, "nowcast_full", checked)}
                />
                <span>{t("admin.users.detail.nowcastGrant")} (<code>nowcast_full</code>)</span>
              </Space>
            </>
          )}
        </Drawer>
      </Wrapper>
    );
  }
}

UsersPanel.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    tier: PropTypes.string.isRequired,
    organization_name: PropTypes.string,
    sign_in_count: PropTypes.number.isRequired,
    disabled: PropTypes.bool.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  invitations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    role: PropTypes.string.isRequired,
    organization_id: PropTypes.number.isRequired,
    organization_name: PropTypes.string.isRequired,
    organization_timezone: PropTypes.string,
    status: PropTypes.string.isRequired,
    last_sent_at: PropTypes.string,
  })).isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    per_page: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  detail: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.number.isRequired,
      email: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      roles: PropTypes.arrayOf(PropTypes.string).isRequired,
      provider: PropTypes.string,
      terms_accepted_at: PropTypes.string,
      organization_timezone: PropTypes.string,
      feature_flags: PropTypes.objectOf(PropTypes.bool),
    }).isRequired,
    entitlements: PropTypes.shape({ tier: PropTypes.string.isRequired }).isRequired,
  }),
  feedback: PropTypes.shape({
    type: PropTypes.string.isRequired,
    operation: PropTypes.string,
    message: PropTypes.string,
    messageKey: PropTypes.string,
    values: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    translatedValues: PropTypes.objectOf(PropTypes.string),
  }),
  fetchUsers: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  clearUserDetail: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  extendTrial: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  resendInvitation: PropTypes.func.isRequired,
  clearFeedback: PropTypes.func.isRequired,
  operator: PropTypes.shape({ email: PropTypes.string.isRequired }),
  mutationLoading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string.isRequired }).isRequired,
};

UsersPanel.defaultProps = {
  error: null,
  detail: null,
  feedback: null,
  operator: null,
};

const mapStateToProps = ({ Admin }) => ({
  rows: Admin.users,
  invitations: Admin.invitations,
  pagination: Admin.usersPagination,
  loading: Admin.usersLoading,
  error: Admin.usersError,
  detail: Admin.userDetail,
  feedback: Admin.feedback,
  operator: Admin.operator,
  mutationLoading: Admin.mutationLoading,
});

const mapDispatchToProps = (dispatch) => ({
  fetchUsers: (params) => dispatch(fetchUsers(params)),
  fetchUser: (id) => dispatch(fetchUser(id)),
  clearUserDetail: () => dispatch(clearUserDetail()),
  updateUser: (id, user, params, feedback) => dispatch(updateUser(id, user, params, feedback)),
  extendTrial: (id, days, reason, params) => dispatch(extendTrial(id, days, reason, params)),
  deleteUser: (id, displayName, params) => dispatch(deleteUser(id, displayName, params)),
  resendInvitation: (organizationId, inviteId, email, source) => dispatch(
    resendInvitation(organizationId, inviteId, email, source),
  ),
  clearFeedback: () => dispatch(clearFeedback()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("common")(UsersPanel));
